import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { User } from '../../services/authService';
import { NovelService, Novel, Chapter } from '../../services/novelService';
import { trackAction } from '../../services/activityService';
import PageHeader from '../common/PageHeader';
import './ChapterReaderPage.css';

interface ChapterReaderPageProps {
  user: User | null;
  onClose: () => void;
  onToggleSidebar: () => void;
}

const ChapterReaderPage: React.FC<ChapterReaderPageProps> = ({ user, onClose, onToggleSidebar }) => {
  const { novelId, chapterId } = useParams<{ novelId: string; chapterId: string }>();
  const navigate = useNavigate();
  
  const [novel, setNovel] = useState<Novel | null>(null);
  const [currentChapter, setCurrentChapter] = useState<Chapter | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showTOC, setShowTOC] = useState(false);

  useEffect(() => {
    if (novelId && chapterId) {
      loadNovelAndChapter();
    }
  }, [novelId, chapterId]);

  useEffect(() => {
    if (currentChapter) {
      trackAction(user, 'NOVEL_READ', {
        novelId: novelId,
        chapterId: chapterId,
        chapterTitle: currentChapter.title,
      });
    }
  }, [currentChapter, novelId, chapterId, user]);

  const loadNovelAndChapter = async () => {
    if (!novelId || !chapterId) return;

    try {
      setLoading(true);
      setError(null);
      
      // Load novel structure (for TOC)
      const novelData = await NovelService.getPublicNovel(novelId);
      setNovel(novelData);
      
      // Load current chapter content
      const chapterData = await NovelService.getPublicChapter(novelId, chapterId);
      setCurrentChapter(chapterData);
    } catch (err) {
      console.error('Error loading chapter:', err);
      setError('Failed to load chapter');
    } finally {
      setLoading(false);
    }
  };

  const handleChapterClick = (newChapterId: string) => {
    navigate(`/novel/public/${novelId}/chapter/${newChapterId}`);
    setShowTOC(false);
  };

  const handlePreviousChapter = () => {
    if (!novel || !currentChapter) return;
    const currentIndex = novel.chapters.findIndex(ch => ch.chapter_id === currentChapter.chapter_id);
    if (currentIndex > 0) {
      handleChapterClick(novel.chapters[currentIndex - 1].chapter_id);
    }
  };

  const handleNextChapter = () => {
    if (!novel || !currentChapter) return;
    const currentIndex = novel.chapters.findIndex(ch => ch.chapter_id === currentChapter.chapter_id);
    if (currentIndex < novel.chapters.length - 1) {
      handleChapterClick(novel.chapters[currentIndex + 1].chapter_id);
    }
  };

  const getCurrentChapterIndex = () => {
    if (!novel || !currentChapter) return -1;
    return novel.chapters.findIndex(ch => ch.chapter_id === currentChapter.chapter_id);
  };

  const hasPreviousChapter = () => {
    return getCurrentChapterIndex() > 0;
  };

  const hasNextChapter = () => {
    if (!novel) return false;
    return getCurrentChapterIndex() < novel.chapters.length - 1;
  };

  if (loading) {
    return (
      <div className="chapter-reader-container">
        <PageHeader
          onToggleSidebar={onToggleSidebar}
          centerContent={<h1 className="page-header-center-title">Loading...</h1>}
        />
        <div className="chapter-reader-loading">Loading chapter...</div>
      </div>
    );
  }

  if (error || !novel || !currentChapter) {
    return (
      <div className="chapter-reader-container">
        <PageHeader
          onToggleSidebar={onToggleSidebar}
          centerContent={<h1 className="page-header-center-title">Error</h1>}
        />
        <div className="chapter-reader-error">
          {error || 'Chapter not found'}
          <button 
            className="chapter-reader-back-button"
            onClick={() => navigate('/novel')}
          >
            Back to Novels
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="chapter-reader-container">
      <PageHeader
        onToggleSidebar={onToggleSidebar}
        leftActions={
          <button 
            className="chapter-reader-back-btn"
            onClick={() => navigate('/novel')}
          >
            ← Back to Novels
          </button>
        }
        centerContent={
          <div className="chapter-reader-header">
            <span className="chapter-reader-novel-title">{novel.title}</span>
            {novel.author_name && (
              <span className="chapter-reader-author">by {novel.author_name}</span>
            )}
          </div>
        }
        rightActions={
          <button 
            className="chapter-reader-toc-btn"
            onClick={() => setShowTOC(!showTOC)}
          >
            {showTOC ? '✕' : '☰'}
          </button>
        }
      />

      <div className="chapter-reader-content">
        {showTOC && (
          <div className="chapter-reader-toc-overlay" onClick={() => setShowTOC(false)}>
            <div className="chapter-reader-toc" onClick={(e) => e.stopPropagation()}>
              <h3 className="chapter-reader-toc-title">Table of Contents</h3>
              <div className="chapter-reader-toc-list">
                {novel.chapters.map((chapter, index) => (
                  <button
                    key={chapter.chapter_id}
                    className={`chapter-reader-toc-item ${chapter.chapter_id === currentChapter.chapter_id ? 'active' : ''}`}
                    onClick={() => handleChapterClick(chapter.chapter_id)}
                  >
                    <span className="chapter-reader-toc-number">{index + 1}.</span>
                    <span className="chapter-reader-toc-text">{chapter.title}</span>
                  </button>
                ))}
              </div>
            </div>
          </div>
        )}

        <div className="chapter-reader-main">
          <div className="chapter-reader-chapter">
            <h1 className="chapter-reader-chapter-title">{currentChapter.title}</h1>
            <div className="chapter-reader-chapter-content">
              <ReactMarkdown 
                remarkPlugins={[remarkGfm]}
                components={{
                  img: ({ node, ...props }) => (
                    <img 
                      {...props} 
                      className="chapter-reader-image"
                      alt={props.alt || ''} 
                    />
                  )
                }}
              >
                {currentChapter.content?.text_value || 'This chapter is empty.'}
              </ReactMarkdown>
            </div>
          </div>

          <div className="chapter-reader-navigation">
            <button
              className="chapter-reader-nav-btn prev"
              onClick={handlePreviousChapter}
              disabled={!hasPreviousChapter()}
            >
              ← Previous
            </button>
            <span className="chapter-reader-nav-position">
              Chapter {getCurrentChapterIndex() + 1} of {novel.chapters.length}
            </span>
            <button
              className="chapter-reader-nav-btn next"
              onClick={handleNextChapter}
              disabled={!hasNextChapter()}
            >
              Next →
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ChapterReaderPage;

